import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { 
  getQuizQuestions, 
  saveQuizQuestions, 
  DEFAULT_QUIZ_QUESTIONS 
} from '../utils/quizData';
import { playCorrectChime, playTickSound } from '../utils/soundEffects';

const { 
  FiX, FiDownload, FiUpload, FiPlus, FiTrash2, FiFileText, 
  FiCheckCircle, FiAlertCircle, FiHelpCircle, FiSearch, FiRefreshCw 
} = FiIcons;

const SAMPLE_TEMPLATE_ROWS = [
  {
    "Category": "Science & Space",
    "Question": "Which Indian space mission made history by landing near the Moon's South Pole in 2023?",
    "Option A": "Mangalyaan",
    "Option B": "Chandrayaan-3",
    "Option C": "Aditya-L1",
    "Option D": "Gaganyaan",
    "Correct Option": "B",
    "Fun Fact": "India became the very first nation in human history to land near the lunar south pole!"
  },
  {
    "Category": "Science & Space",
    "Question": "What green pigment in plant leaves absorbs sunlight for photosynthesis?",
    "Option A": "Carotene",
    "Option B": "Hemoglobin",
    "Option C": "Chlorophyll",
    "Option D": "Melanin",
    "Correct Option": "C",
    "Fun Fact": "Chlorophyll traps solar energy to produce food and release life-giving oxygen."
  },
  {
    "Category": "Indian History",
    "Question": "Who was the brave Queen of Jhansi who fought valiantly in the 1857 Rebellion?",
    "Option A": "Rani Padmavati",
    "Option B": "Rani Lakshmibai",
    "Option C": "Razia Sultana",
    "Option D": "Kittur Chennamma",
    "Correct Option": "B",
    "Fun Fact": "Rani Lakshmibai fought with her adopted son tied to her back, becoming an eternal icon of courage."
  },
  {
    "Category": "General Knowledge",
    "Question": "The Ashoka Chakra on India's National Tricolor Flag contains how many spokes?",
    "Option A": "12 Spokes",
    "Option B": "18 Spokes",
    "Option C": "24 Spokes",
    "Option D": "32 Spokes",
    "Correct Option": "C",
    "Fun Fact": "The 24 spokes represent the 24 eternal virtues and 24 hours of continuous progress."
  },
  {
    "Category": "Space & Astronomy",
    "Question": "What is the gravitational pull on the Moon compared to Earth?",
    "Option A": "Same as Earth",
    "Option B": "1/6th of Earth's Gravity",
    "Option C": "Double of Earth's Gravity",
    "Option D": "Zero Gravity",
    "Correct Option": "B",
    "Fun Fact": "Because the Moon's gravity is 1/6th of Earth, humans can jump 6 times higher on the Moon!"
  }
];

export const downloadQuizExcelSample = () => {
  try {
    const ws = XLSX.utils.json_to_sheet(SAMPLE_TEMPLATE_ROWS);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Quiz Questions");
    XLSX.writeFile(wb, "sample_quiz_questions.xlsx");
    playTickSound(880, 0.05);
  } catch (e) {
    console.error("Excel download error", e);
    alert("Failed to generate Excel file: " + e.message);
  }
};

export default function QuizQuestionManagerModal({ isOpen, onClose, onQuestionsUpdated, initialTab = 'upload' }) {
  const [activeTab, setActiveTab] = useState(initialTab || 'upload'); // 'upload' | 'manual' | 'bank'
  const [questions, setQuestions] = useState(getQuizQuestions());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilterCategory, setSelectedFilterCategory] = useState('All');

  // Upload state
  const [uploadMode, setUploadMode] = useState('append'); // 'append' | 'replace'
  const [parsedPreview, setParsedPreview] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null); // { type: 'success' | 'error', message: '' }
  const fileInputRef = useRef(null);

  // Manual Add Form State
  const [newQuestion, setNewQuestion] = useState({
    category: 'Science & Space',
    question: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctIndex: 0,
    fact: ''
  });

  useEffect(() => {
    if (isOpen) {
      if (initialTab) setActiveTab(initialTab);
      setQuestions(getQuizQuestions());
      setParsedPreview(null);
      setUploadStatus(null);
    }
  }, [isOpen, initialTab]);

  if (!isOpen) return null;

  const handleDownloadExcel = downloadQuizExcelSample;

  // 2. Download Sample CSV (.csv)
  const handleDownloadCSV = () => {
    try {
      const ws = XLSX.utils.json_to_sheet(SAMPLE_TEMPLATE_ROWS);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", "sample_quiz_questions.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      playTickSound(880, 0.05);
    } catch (e) {
      console.error("CSV download error", e);
      alert("Failed to generate CSV file: " + e.message);
    }
  };

  // 3. File Upload and Parsing
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rawJson = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        if (!rawJson || rawJson.length === 0) {
          setUploadStatus({
            type: 'error',
            message: 'The uploaded file appears to be empty. Please ensure data exists in the first sheet.'
          });
          return;
        }

        // Map flexible column headers
        const parsed = [];
        let skippedCount = 0;

        rawJson.forEach((row, index) => {
          // Flexible key lookup
          const findKey = (candidates) => {
            const keys = Object.keys(row);
            for (const cand of candidates) {
              const matched = keys.find(k => k.trim().toLowerCase() === cand.toLowerCase());
              if (matched && String(row[matched]).trim()) return String(row[matched]).trim();
            }
            return '';
          };

          const questionText = findKey(['question', 'question text', 'q', 'question_title']);
          const category = findKey(['category', 'subject', 'topic']) || 'General Knowledge';
          const optA = findKey(['option a', 'option 1', 'a', 'opt a', 'option_a']);
          const optB = findKey(['option b', 'option 2', 'b', 'opt b', 'option_b']);
          const optC = findKey(['option c', 'option 3', 'c', 'opt c', 'option_c']);
          const optD = findKey(['option d', 'option 4', 'd', 'opt d', 'option_d']);
          const correctKey = findKey(['correct option', 'correct answer', 'answer', 'correct', 'correct_option']);
          const fact = findKey(['fun fact', 'fact', 'explanation', 'insight', 'clue']);

          if (!questionText || !optA || !optB) {
            skippedCount++;
            return;
          }

          const options = [optA, optB];
          if (optC) options.push(optC);
          if (optD) options.push(optD);

          // Resolve correct index: check if A/B/C/D, 1/2/3/4, or exact text match
          let correctIndex = 0;
          const cleanKey = correctKey.toLowerCase().replace(/option\s*/, '').trim();

          if (cleanKey === 'a' || cleanKey === '1' || cleanKey === '0') correctIndex = 0;
          else if (cleanKey === 'b' || cleanKey === '2') correctIndex = 1;
          else if (cleanKey === 'c' || cleanKey === '3') correctIndex = Math.min(2, options.length - 1);
          else if (cleanKey === 'd' || cleanKey === '4') correctIndex = Math.min(3, options.length - 1);
          else {
            // Check text match with options
            const foundIdx = options.findIndex(o => o.toLowerCase() === correctKey.toLowerCase());
            if (foundIdx >= 0) correctIndex = foundIdx;
          }

          parsed.push({
            id: 'custom_' + Date.now() + '_' + index,
            category: category,
            type: 'trivia',
            question: questionText,
            options: options,
            correctIndex: correctIndex,
            fact: fact || 'Great job answering this trivia question!'
          });
        });

        if (parsed.length === 0) {
          setUploadStatus({
            type: 'error',
            message: 'No valid questions could be extracted. Please check that headers match the sample template.'
          });
          setParsedPreview(null);
        } else {
          setParsedPreview(parsed);
          setUploadStatus({
            type: 'success',
            message: `Successfully processed ${parsed.length} questions from "${file.name}"! ${
              skippedCount > 0 ? `(${skippedCount} blank rows skipped)` : ''
            }`
          });
          playCorrectChime();
        }
      } catch (err) {
        console.error('File parsing error', err);
        setUploadStatus({
          type: 'error',
          message: 'Failed to parse file: ' + err.message
        });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // 4. Save parsed questions to storage
  const handleApplyUploadedQuestions = () => {
    if (!parsedPreview || parsedPreview.length === 0) return;

    let finalQuestions = [];
    if (uploadMode === 'append') {
      finalQuestions = [...questions, ...parsedPreview];
    } else {
      finalQuestions = parsedPreview;
    }

    saveQuizQuestions(finalQuestions);
    setQuestions(finalQuestions);
    setParsedPreview(null);
    setUploadStatus({
      type: 'success',
      message: `Updated! Question Bank now has ${finalQuestions.length} total questions.`
    });
    playCorrectChime();
    if (onQuestionsUpdated) onQuestionsUpdated();
  };

  // 5. Add Single Manual Question
  const handleManualAddSubmit = (e) => {
    e.preventDefault();
    if (!newQuestion.question.trim() || !newQuestion.optionA.trim() || !newQuestion.optionB.trim()) {
      alert('Please fill in at least the Question and Options A and B.');
      return;
    }

    const options = [newQuestion.optionA.trim(), newQuestion.optionB.trim()];
    if (newQuestion.optionC.trim()) options.push(newQuestion.optionC.trim());
    if (newQuestion.optionD.trim()) options.push(newQuestion.optionD.trim());

    const item = {
      id: 'custom_' + Date.now(),
      category: newQuestion.category.trim() || 'General Knowledge',
      type: 'trivia',
      question: newQuestion.question.trim(),
      options: options,
      correctIndex: Math.min(newQuestion.correctIndex, options.length - 1),
      fact: newQuestion.fact.trim() || 'Knowledge is power!'
    };

    const updated = [item, ...questions];
    saveQuizQuestions(updated);
    setQuestions(updated);
    playCorrectChime();
    if (onQuestionsUpdated) onQuestionsUpdated();

    // Reset Form
    setNewQuestion({
      category: newQuestion.category,
      question: '',
      optionA: '',
      optionB: '',
      optionC: '',
      optionD: '',
      correctIndex: 0,
      fact: ''
    });

    alert('Question added successfully!');
  };

  // 6. Delete Individual Question
  const handleDeleteQuestion = (id) => {
    if (!window.confirm('Delete this question from the Question Bank?')) return;
    const updated = questions.filter(q => q.id !== id);
    saveQuizQuestions(updated);
    setQuestions(updated);
    if (onQuestionsUpdated) onQuestionsUpdated();
  };

  // 7. Reset to Default Questions
  const handleResetToDefaults = () => {
    if (!window.confirm('Reset all questions to the original default collection (15 curated questions)?')) return;
    saveQuizQuestions(DEFAULT_QUIZ_QUESTIONS);
    setQuestions(DEFAULT_QUIZ_QUESTIONS);
    if (onQuestionsUpdated) onQuestionsUpdated();
    alert('Questions reset to default!');
  };

  // Categories for filter
  const categories = ['All', ...new Set(questions.map(q => q.category).filter(Boolean))];

  const filteredQuestions = questions.filter(q => {
    const matchesCat = selectedFilterCategory === 'All' || q.category === selectedFilterCategory;
    const matchesSearch = !searchTerm || q.question.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCat && matchesSearch;
  });

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-sm animate-fadeIn">
      <div className="relative w-full max-w-4xl max-h-[92vh] flex flex-col rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden">
        
        {/* Modal Header */}
        <div className="p-4 sm:p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3 bg-gradient-to-r from-amber-500/10 via-yellow-500/5 to-indigo-500/10">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">📋</span>
              <h2 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white">
                School Quiz & Science Question Manager
              </h2>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Upload custom questions via Excel / CSV, download sample templates, or edit the live question bank.
            </p>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        {/* Tab Strip */}
        <div className="px-4 sm:px-6 pt-3 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2 bg-slate-50 dark:bg-slate-900/40">
          <button
            onClick={() => setActiveTab('upload')}
            className={`px-4 py-2.5 text-xs font-bold rounded-t-xl transition-all flex items-center gap-2 border-b-2 ${
              activeTab === 'upload'
                ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-white dark:bg-slate-800 shadow-sm'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'
            }`}
          >
            <SafeIcon icon={FiUpload} />
            <span>Excel / CSV Upload & Templates</span>
          </button>

          <button
            onClick={() => setActiveTab('bank')}
            className={`px-4 py-2.5 text-xs font-bold rounded-t-xl transition-all flex items-center gap-2 border-b-2 ${
              activeTab === 'bank'
                ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-white dark:bg-slate-800 shadow-sm'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'
            }`}
          >
            <SafeIcon icon={FiFileText} />
            <span>Active Question Bank ({questions.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('manual')}
            className={`px-4 py-2.5 text-xs font-bold rounded-t-xl transition-all flex items-center gap-2 border-b-2 ${
              activeTab === 'manual'
                ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-white dark:bg-slate-800 shadow-sm'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'
            }`}
          >
            <SafeIcon icon={FiPlus} />
            <span>Add Single Question</span>
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 p-4 sm:p-6 overflow-y-auto max-h-[calc(92vh-160px)]">

          {/* TAB 1: SPREADSHEET UPLOAD & TEMPLATES */}
          {activeTab === 'upload' && (
            <div className="space-y-6">
              
              {/* Step 1: Download Templates */}
              <div className="p-5 rounded-2xl border border-amber-200 dark:border-amber-500/30 bg-amber-50/50 dark:bg-amber-500/5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <span className="text-[10px] font-black uppercase text-amber-600 dark:text-amber-400 tracking-wider block">
                      Step 1 • Get Formatted Template
                    </span>
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">
                      Download Sample Question Sheet
                    </h3>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 max-w-xl">
                      Pre-formatted template with standard columns: <strong>Category, Question, Option A, Option B, Option C, Option D, Correct Option, Fun Fact</strong>.
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={handleDownloadExcel}
                      className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-2 shadow-md transition-all"
                    >
                      <SafeIcon icon={FiDownload} />
                      <span>Download .XLSX</span>
                    </button>

                    <button
                      type="button"
                      onClick={handleDownloadCSV}
                      className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 dark:bg-slate-700 text-white font-bold text-xs flex items-center gap-2 shadow-md transition-all"
                    >
                      <SafeIcon icon={FiDownload} />
                      <span>Download .CSV</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Step 2: Upload Area */}
              <div className="p-6 rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/30 text-center flex flex-col items-center justify-center">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".xlsx, .xls, .csv"
                  className="hidden"
                />

                <div className="w-14 h-14 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center text-2xl mb-3">
                  <SafeIcon icon={FiUpload} />
                </div>

                <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                  Upload your completed Excel or CSV Sheet
                </h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-md">
                  Supports Microsoft Excel (.xlsx, .xls) and standard comma-separated values (.csv) exported from Google Sheets or Excel.
                </p>

                <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg hover:brightness-105 active:scale-95 transition-all"
                  >
                    Select File From Device 📁
                  </button>
                </div>
              </div>

              {/* Upload Status Alert */}
              {uploadStatus && (
                <div className={`p-4 rounded-2xl border text-xs flex items-start gap-3 ${
                  uploadStatus.type === 'success'
                    ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300'
                    : 'bg-rose-50 dark:bg-rose-950/40 border-rose-300 dark:border-rose-800 text-rose-800 dark:text-rose-300'
                }`}>
                  <SafeIcon 
                    icon={uploadStatus.type === 'success' ? FiCheckCircle : FiAlertCircle} 
                    className="text-lg flex-shrink-0 mt-0.5" 
                  />
                  <div>
                    <strong className="block font-bold">
                      {uploadStatus.type === 'success' ? 'Sheet Validated' : 'Parsing Notice'}
                    </strong>
                    <span>{uploadStatus.message}</span>
                  </div>
                </div>
              )}

              {/* Preview of Parsed Rows */}
              {parsedPreview && (
                <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 shadow-lg space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200 dark:border-slate-700">
                    <div>
                      <h4 className="text-sm font-black text-slate-900 dark:text-white">
                        Preview Questions ({parsedPreview.length} items detected)
                      </h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Choose whether to append these questions or completely replace your current question bank.
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <label className="text-xs font-bold text-slate-600 dark:text-slate-300 flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="uploadMode"
                          checked={uploadMode === 'append'}
                          onChange={() => setUploadMode('append')}
                          className="accent-amber-500"
                        />
                        Append to existing
                      </label>
                      <label className="text-xs font-bold text-slate-600 dark:text-slate-300 flex items-center gap-1.5 cursor-pointer ml-2">
                        <input
                          type="radio"
                          name="uploadMode"
                          checked={uploadMode === 'replace'}
                          onChange={() => setUploadMode('replace')}
                          className="accent-amber-500"
                        />
                        Replace all
                      </label>
                    </div>
                  </div>

                  <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
                    {parsedPreview.map((q, idx) => (
                      <div key={idx} className="p-3 rounded-xl border border-slate-100 dark:border-slate-700/80 bg-slate-50 dark:bg-slate-900/60 text-xs">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase bg-amber-500/20 text-amber-600 dark:text-amber-400">
                            {q.category}
                          </span>
                          <span className="font-bold text-slate-800 dark:text-slate-200">#{idx + 1}. {q.question}</span>
                        </div>
                        <div className="flex flex-wrap gap-2 text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">
                          {q.options.map((opt, oIdx) => (
                            <span 
                              key={oIdx}
                              className={`px-2 py-0.5 rounded border ${
                                oIdx === q.correctIndex
                                  ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-700 dark:text-emerald-400 font-bold'
                                  : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                              }`}
                            >
                              {String.fromCharCode(65 + oIdx)}: {opt}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setParsedPreview(null)}
                      className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                    >
                      Cancel Preview
                    </button>
                    <button
                      type="button"
                      onClick={handleApplyUploadedQuestions}
                      className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-black text-xs uppercase tracking-wider shadow-lg"
                    >
                      Save & Apply {parsedPreview.length} Questions 💾
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: ACTIVE QUESTION BANK TABLE */}
          {activeTab === 'bank' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-1 max-w-md">
                  <div className="relative flex-1">
                    <SafeIcon icon={FiSearch} className="absolute left-3 top-3 text-slate-400 text-sm" />
                    <input
                      type="text"
                      placeholder="Search questions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs font-bold text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>

                  <select
                    value={selectedFilterCategory}
                    onChange={(e) => setSelectedFilterCategory(e.target.value)}
                    className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs font-bold text-slate-700 dark:text-slate-200"
                  >
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleResetToDefaults}
                    className="px-3 py-2 rounded-xl border border-rose-200 dark:border-rose-900/60 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 text-xs font-bold flex items-center gap-1.5 transition-colors"
                    title="Restore default Indian school curriculum questions"
                  >
                    <SafeIcon icon={FiRefreshCw} />
                    <span>Reset Defaults</span>
                  </button>
                </div>
              </div>

              <div className="border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden shadow-sm">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-100 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 font-bold uppercase text-[10px]">
                    <tr>
                      <th className="py-3 px-3">#</th>
                      <th className="py-3 px-3">Category</th>
                      <th className="py-3 px-3">Question</th>
                      <th className="py-3 px-3">Correct Option</th>
                      <th className="py-3 px-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {filteredQuestions.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-slate-400">
                          No matching questions found in this category.
                        </td>
                      </tr>
                    ) : (
                      filteredQuestions.map((q, idx) => (
                        <tr key={q.id || idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                          <td className="py-3 px-3 font-bold text-slate-400">{idx + 1}</td>
                          <td className="py-3 px-3">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                              {q.category}
                            </span>
                          </td>
                          <td className="py-3 px-3 font-medium text-slate-800 dark:text-slate-200 max-w-sm">
                            <p className="line-clamp-2">{q.question}</p>
                            {q.fact && (
                              <span className="text-[10px] text-slate-400 italic block mt-0.5 line-clamp-1">
                                💡 {q.fact}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-3 font-bold text-emerald-600 dark:text-emerald-400">
                            {q.options ? q.options[q.correctIndex] || `Option ${q.correctIndex + 1}` : q.answer || 'Answer'}
                          </td>
                          <td className="py-3 px-3 text-right">
                            <button
                              type="button"
                              onClick={() => handleDeleteQuestion(q.id)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
                              title="Delete Question"
                            >
                              <SafeIcon icon={FiTrash2} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 3: MANUAL ADD FORM */}
          {activeTab === 'manual' && (
            <form onSubmit={handleManualAddSubmit} className="space-y-4 max-w-2xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                    Subject / Category *
                  </label>
                  <select
                    value={newQuestion.category}
                    onChange={(e) => setNewQuestion({ ...newQuestion, category: e.target.value })}
                    className="w-full p-2.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-bold text-slate-800 dark:text-white"
                  >
                    <option>Science & Space</option>
                    <option>Indian History & Heritage</option>
                    <option>General Knowledge & Nature</option>
                    <option>Mathematics & Logic</option>
                    <option>Sports & Champions</option>
                    <option>Literature & Arts</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                    Correct Option *
                  </label>
                  <select
                    value={newQuestion.correctIndex}
                    onChange={(e) => setNewQuestion({ ...newQuestion, correctIndex: Number(e.target.value) })}
                    className="w-full p-2.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-bold text-slate-800 dark:text-white"
                  >
                    <option value={0}>Option A is Correct</option>
                    <option value={1}>Option B is Correct</option>
                    <option value={2}>Option C is Correct</option>
                    <option value={3}>Option D is Correct</option>
                  </select>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                    Question Text *
                  </label>
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                    <span className="font-semibold">Quick Emojis:</span>
                    {['🔬', '🚀', '🪐', '🇮🇳', '🏛️', '🌿', '📐', '⚡', '🏆', '📚', '🌍', '🧠', '🎯', '💡'].map((em, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setNewQuestion(prev => ({ ...prev, question: (prev.question || '') + ' ' + em }))}
                        className="hover:scale-125 transition-transform cursor-pointer text-sm"
                        title={`Insert ${em}`}
                      >
                        {em}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea
                  required
                  rows={2}
                  value={newQuestion.question}
                  onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
                  placeholder="e.g. Which layer of the atmosphere protects Earth from harmful UV rays? 🌍"
                  className="w-full p-2.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-bold text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400 block mb-1">Option A *</label>
                  <input
                    type="text"
                    required
                    value={newQuestion.optionA}
                    onChange={(e) => setNewQuestion({ ...newQuestion, optionA: e.target.value })}
                    placeholder="Option A text"
                    className="w-full p-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-medium text-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400 block mb-1">Option B *</label>
                  <input
                    type="text"
                    required
                    value={newQuestion.optionB}
                    onChange={(e) => setNewQuestion({ ...newQuestion, optionB: e.target.value })}
                    placeholder="Option B text"
                    className="w-full p-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-medium text-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400 block mb-1">Option C (Optional)</label>
                  <input
                    type="text"
                    value={newQuestion.optionC}
                    onChange={(e) => setNewQuestion({ ...newQuestion, optionC: e.target.value })}
                    placeholder="Option C text"
                    className="w-full p-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-medium text-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400 block mb-1">Option D (Optional)</label>
                  <input
                    type="text"
                    value={newQuestion.optionD}
                    onChange={(e) => setNewQuestion({ ...newQuestion, optionD: e.target.value })}
                    placeholder="Option D text"
                    className="w-full p-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-medium text-slate-800 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                  Fun Fact / Educational Insight (Shown on correct answer)
                </label>
                <input
                  type="text"
                  value={newQuestion.fact}
                  onChange={(e) => setNewQuestion({ ...newQuestion, fact: e.target.value })}
                  placeholder="e.g. The Ozone layer is located in the stratosphere about 15 to 35 km above Earth."
                  className="w-full p-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-medium text-slate-800 dark:text-white"
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg hover:brightness-105 active:scale-95 transition-all mt-4"
              >
                Add Question To Bank ➕
              </button>
            </form>
          )}

        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 flex items-center justify-between text-xs text-slate-500">
          <span>Active Question Bank: <strong>{questions.length} Questions</strong></span>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-200 dark:bg-slate-800 text-slate-800 dark:text-slate-200 font-bold hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors"
          >
            Close
          </button>
        </div>

      </div>
    </div>
  );
}
